import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import SignupPage from '@/pages/SignupPage';
import TeacherDashboardPage from '@/pages/TeacherDashboardPage';
import TeacherStudentsPage from '@/pages/TeacherStudentsPage';
import TeacherBookingsPage from '@/pages/TeacherBookingsPage';
import TeacherPortfolioPage from '@/pages/TeacherPortfolioPage';
import TeacherBlogsPage from '@/pages/TeacherBlogsPage';
import TeacherPackagesPage from '@/pages/TeacherPackagesPage';
import TeacherPaymentsPage from '@/pages/TeacherPaymentsPage';
import TeacherAiPracticePage from '@/pages/TeacherAiPracticePage';
import StudentDashboardPage from '@/pages/StudentDashboardPage';
import StudentOverviewPage from '@/pages/StudentOverviewPage';
import StudentFeaturePlaceholderPage from '@/pages/StudentFeaturePlaceholderPage';
import StudentBillingPage from '@/pages/StudentBillingPage';
import StudentSubscriptionsPage from '@/pages/StudentSubscriptionsPage';
import TenantsPage from '@/pages/TenantsPage';
import SuperadminTenantFeaturesPage from '@/pages/SuperadminTenantFeaturesPage';
import SuperadminTenantUsersPage from '@/pages/SuperadminTenantUsersPage';
import SuperadminTenantPaymentsPage from '@/pages/SuperadminTenantPaymentsPage';
import SuperadminSaasPlansPage from '@/pages/SuperadminSaasPlansPage';
import SuperadminRefundRequestsPage from '@/pages/SuperadminRefundRequestsPage';
import SuperadminBillingActivityPage from '@/pages/SuperadminBillingActivityPage';
import DashboardLayout from '@/components/DashboardLayout';
import { useFeatures } from '@/hooks/useFeatures';
import { authService } from '@/services/auth';
import api from '@/services/api';

const App: React.FC = () => {
  return (
    <Router>
      <div className="relative min-h-screen flex flex-col overflow-hidden">
        <div className="relative z-10 min-h-screen flex flex-col">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/" element={<DashboardRoot />} />
            <Route path="/users" element={<Navigate to="/students" replace />} />
            <Route path="/students" element={<StudentsRoot />} />
            <Route path="/bookings" element={<BookingsRoot />} />
            <Route path="/packages" element={<PackagesRoot />} />
            <Route path="/payments" element={<PaymentsRoot />} />
            <Route path="/courses" element={<Navigate to="/" replace />} />
            <Route path="/ai-exercises" element={<AiExercisesRoot />} />
            <Route path="/newsletter" element={<Navigate to="/" replace />} />
            <Route path="/blogs" element={<BlogsRoot />} />
            <Route path="/portfolio" element={<PortfolioRoot />} />
            <Route path="/billing" element={<BillingRoot />} />
            <Route path="/billing/subscriptions" element={<BillingSubscriptionsRoot />} />
            <Route path="/billing/payments" element={<BillingPaymentsRoot />} />
            <Route path="/meetings" element={<Navigate to="/bookings" replace />} />
            <Route path="/analytics" element={<Navigate to="/" replace />} />
            <Route path="/reports" element={<Navigate to="/" replace />} />
            <Route path="/settings" element={<Navigate to="/" replace />} />
            <Route path="/profile" element={<Navigate to="/" replace />} />
            <Route path="/integrations" element={<Navigate to="/" replace />} />
            <Route path="/team" element={<Navigate to="/" replace />} />
            <Route path="/notifications" element={<Navigate to="/" replace />} />
            <Route path="/help" element={<Navigate to="/" replace />} />
            <Route path="/tenants" element={<TenantsRoot />} />
            <Route path="/tenants/features" element={<SuperadminFeatureRoot />} />
            <Route path="/tenants/users" element={<SuperadminUsersRoot />} />
            <Route path="/tenants/payments" element={<SuperadminPaymentsRoot />} />
            <Route path="/tenants/saas-plans" element={<SuperadminSaasPlansRoot />} />
            <Route path="/tenants/refunds" element={<SuperadminRefundsRoot />} />
            <Route path="/tenants/billing-activity" element={<SuperadminBillingActivityRoot />} />
          </Routes>
          <Toaster />
        </div>
      </div>
    </Router>
  );
};

// Root dashboard route that reads auth directly from cookie
const DashboardRoot: React.FC = () => {
  const [user, setUser] = React.useState(authService.getUser());
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const checkAuth = async () => {
      // First check if user cookie exists (this is readable by JavaScript)
      const currentUser = authService.getUser();
      
      if (currentUser) {
        console.log('[DashboardRoot] Found user cookie, using it');
        setUser(currentUser);
        setLoading(false);
        return;
      }

      // If user cookie doesn't exist, try to fetch from API
      // The browser will automatically send the HttpOnly token cookie with the request
      console.log('[DashboardRoot] No user cookie found, trying to fetch from API...');
      try {
        const response = await api.get('/auth/me');
        // Normalize the user data (handle both PascalCase and camelCase)
        const raw = response.data;
        const fetchedUser = {
          id: raw.id ?? raw.Id,
          email: raw.email ?? raw.Email,
          firstName: raw.firstName ?? raw.FirstName ?? null,
          lastName: raw.lastName ?? raw.LastName ?? null,
          avatarUrl: raw.avatarUrl ?? raw.AvatarUrl ?? null,
          role: raw.role ?? raw.Role,
          tenantId: raw.tenantId ?? raw.TenantId ?? null,
          createdAt: raw.createdAt ?? raw.CreatedAt,
        };
        console.log('[DashboardRoot] Fetched user from API (token was valid)', fetchedUser);
        setUser(fetchedUser);
      } catch (error) {
        console.error('[DashboardRoot] Failed to fetch user from API (no valid token):', error);
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login page
    return <Navigate to="/login" replace />;
  }

  switch (user.role?.toLowerCase()) {
    case 'superadmin':
      return <Navigate to="/tenants" replace />;
    case 'teacher':
      return (
        <DashboardLayout>
          <TeacherDashboardPage />
        </DashboardLayout>
      );
    case 'student':
      return (
        <DashboardLayout>
          <StudentOverviewPage />
        </DashboardLayout>
      );
    default:
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center text-gray-300">Unknown role. Please contact support.</div>
        </div>
      );
  }
};

// Tenants route with simple cookie-based guard
const TenantsRoot: React.FC = () => {
  const [user, setUser] = React.useState(authService.getUser());
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const checkAuth = async () => {
      const currentUser = authService.getUser();
      
      if (currentUser) {
        setUser(currentUser);
        setLoading(false);
        return;
      }

      // If user cookie doesn't exist, try to fetch from API
      try {
        const response = await api.get('/auth/me');
        const raw = response.data;
        const fetchedUser = {
          id: raw.id ?? raw.Id,
          email: raw.email ?? raw.Email,
          firstName: raw.firstName ?? raw.FirstName ?? null,
          lastName: raw.lastName ?? raw.LastName ?? null,
          avatarUrl: raw.avatarUrl ?? raw.AvatarUrl ?? null,
          role: raw.role ?? raw.Role,
          tenantId: raw.tenantId ?? raw.TenantId ?? null,
          createdAt: raw.createdAt ?? raw.CreatedAt,
        };
        setUser(fetchedUser);
      } catch (error) {
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login page
    return <Navigate to="/login" replace />;
  }

  if ((user.role || '').toLowerCase() !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  return <TenantsPage />;
};

const SuperadminFeatureRoot: React.FC = () => {
  const user = authService.getUser();
  if (!user) return <Navigate to="/login" replace />;
  if ((user.role || '').toLowerCase() !== 'superadmin') return <Navigate to="/" replace />;
  return <SuperadminTenantFeaturesPage />;
};

const SuperadminUsersRoot: React.FC = () => {
  const user = authService.getUser();
  if (!user) return <Navigate to="/login" replace />;
  if ((user.role || '').toLowerCase() !== 'superadmin') return <Navigate to="/" replace />;
  return <SuperadminTenantUsersPage />;
};

const SuperadminPaymentsRoot: React.FC = () => {
  const user = authService.getUser();
  if (!user) return <Navigate to="/login" replace />;
  if ((user.role || '').toLowerCase() !== 'superadmin') return <Navigate to="/" replace />;
  return <SuperadminTenantPaymentsPage />;
};

const SuperadminSaasPlansRoot: React.FC = () => {
  const user = authService.getUser();
  if (!user) return <Navigate to="/login" replace />;
  if ((user.role || '').toLowerCase() !== 'superadmin') return <Navigate to="/" replace />;
  return <SuperadminSaasPlansPage />;
};

const SuperadminRefundsRoot: React.FC = () => {
  const user = authService.getUser();
  if (!user) return <Navigate to="/login" replace />;
  if ((user.role || '').toLowerCase() !== 'superadmin') return <Navigate to="/" replace />;
  return <SuperadminRefundRequestsPage />;
};

const SuperadminBillingActivityRoot: React.FC = () => {
  const user = authService.getUser();
  if (!user) return <Navigate to="/login" replace />;
  if ((user.role || '').toLowerCase() !== 'superadmin') return <Navigate to="/" replace />;
  return <SuperadminBillingActivityPage />;
};

const StudentsRoot: React.FC = () => (
  <TeacherOnlyRoot>
    <TeacherStudentsPage />
  </TeacherOnlyRoot>
);

const PackagesRoot: React.FC = () => (
  <TeacherOnlyRoot>
    <TeacherPackagesPage />
  </TeacherOnlyRoot>
);

const BookingsRoot: React.FC = () => {
  const user = authService.getUser();
  if (!user) return <Navigate to="/login" replace />;

  const role = (user.role || '').toLowerCase();
  if (role === 'teacher') {
    return (
      <TeacherOnlyRoot>
        <TeacherBookingsPage />
      </TeacherOnlyRoot>
    );
  }
  if (role === 'student') {
    return (
      <StudentOnlyRoot featureKey="bookings">
        <StudentDashboardPage />
      </StudentOnlyRoot>
    );
  }

  return <Navigate to="/" replace />;
};

const PaymentsRoot: React.FC = () => (
  <TeacherOnlyRoot>
    <TeacherPaymentsPage />
  </TeacherOnlyRoot>
);

const AiExercisesRoot: React.FC = () => {
  const user = authService.getUser();
  if (!user) return <Navigate to="/login" replace />;

  const role = (user.role || '').toLowerCase();
  if (role === 'teacher') {
    return (
      <TeacherOnlyRoot>
        <TeacherAiPracticePage />
      </TeacherOnlyRoot>
    );
  }

  if (role === 'student') {
    return (
      <StudentOnlyRoot featureKey="ai_exercises">
        <StudentFeaturePlaceholderPage
          title="AI Exercises"
          description="Practice with AI-guided exercises tailored to your level."
        />
      </StudentOnlyRoot>
    );
  }

  return <Navigate to="/" replace />;
};

const BlogsRoot: React.FC = () => (
  <TeacherOnlyRoot>
    <TeacherBlogsPage />
  </TeacherOnlyRoot>
);

const PortfolioRoot: React.FC = () => (
  <TeacherOnlyRoot>
    <TeacherPortfolioPage />
  </TeacherOnlyRoot>
);

const BillingRoot: React.FC = () => (
  <StudentOnlyRoot featureKey="billing">
    <StudentBillingPage />
  </StudentOnlyRoot>
);

const BillingSubscriptionsRoot: React.FC = () => (
  <StudentOnlyRoot featureKey="subscriptions">
    <StudentSubscriptionsPage />
  </StudentOnlyRoot>
);

const BillingPaymentsRoot: React.FC = () => (
  <StudentOnlyRoot featureKey="payments">
    <StudentFeaturePlaceholderPage
      title="Payments"
      description="Track invoices and payment history for your account."
    />
  </StudentOnlyRoot>
);

const FeatureGuard: React.FC<{
  featureKey?: string;
  enabled: boolean;
  children: React.ReactNode;
}> = ({ featureKey, enabled, children }) => {
  const { enabledFeatures, isLoading, isVerified } = useFeatures({ enabled: enabled && Boolean(featureKey) });
  const normalizeFeatureKey = (key: string | undefined | null): string => {
    if (!key) return '';
    const normalized = key.toLowerCase().replace(/-/g, '_');
    if (normalized === 'students') return 'users';
    if (normalized === 'blog') return 'blogs';
    if (normalized === 'ai_exercise') return 'ai_exercises';
    return normalized;
  };

  if (!featureKey || !enabled) {
    return <>{children}</>;
  }

  if (isLoading || !isVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const normalizedEnabled = new Set((enabledFeatures || []).map((key) => normalizeFeatureKey(key)));
  if (!normalizedEnabled.has(normalizeFeatureKey(featureKey))) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const TeacherOnlyRoot: React.FC<{
  children: React.ReactNode;
  superadminRedirect?: string;
  featureKey?: string;
}> = ({ children, superadminRedirect = '/tenants', featureKey }) => {
  const user = authService.getUser();
  if (!user) return <Navigate to="/login" replace />;

  const role = (user.role || '').toLowerCase();
  if (role === 'superadmin') return <Navigate to={superadminRedirect} replace />;
  if (role !== 'teacher') return <Navigate to="/" replace />;

  return (
    <FeatureGuard featureKey={featureKey} enabled={role === 'teacher'}>
      <DashboardLayout>{children}</DashboardLayout>
    </FeatureGuard>
  );
};

const StudentOnlyRoot: React.FC<{ children: React.ReactNode; featureKey?: string }> = ({
  children,
  featureKey,
}) => {
  const user = authService.getUser();
  if (!user) return <Navigate to="/login" replace />;

  const role = (user.role || '').toLowerCase();
  if (role === 'superadmin') return <Navigate to="/tenants" replace />;
  if (role !== 'student') return <Navigate to="/" replace />;

  return (
    <FeatureGuard featureKey={featureKey} enabled={role === 'student'}>
      <DashboardLayout>{children}</DashboardLayout>
    </FeatureGuard>
  );
};

export default App;


